import React, { useState, useMemo, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Eye,
  Edit,
  MapPin,
  Building2,
  User,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  Settings,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ClienteWizard from '../components/ClienteWizard';
import { useToast } from '../hooks/useToast';
import { useClientes } from '../hooks/useClientes';
import { useTiposPlato } from '../hooks/useRecetas';
import type { TipoPlato } from '../services/recetasAPI';
import { parseCategoriasAccesoV2 } from '@suminuta/shared';
import { clientesAPI } from '../services/clientesAPI';

// Interfaces para el módulo de clientes
interface Cliente {
  id: number;
  nombre: string;
  rut: string;
  email: string;
  nombre_contacto: string;
  telefono_contacto?: string;
  tipo_cliente: 'persona_natural' | 'juridica';
  razon_social?: string;
  representante_legal?: string;
  observaciones_despacho?: string;
  // Shape persistido en BD: v2 `{ tipos, opciones?, bebidas? }` o legacy
  // `number[]`. El wizard parsea con parseCategoriasAccesoV2.
  categorias_acceso: unknown;
  usuario_id?: number;
  activo: boolean;
  created_at: Date;
  updated_at: Date;
}

interface ClienteDireccion {
  id: number;
  cliente_id: number;
  nombre_direccion: string;
  direccion: string;
  comuna?: string;
  ciudad?: string;
  codigo_postal?: string;
  referencia?: string;
  contacto_nombre?: string;
  contacto_telefono?: string;
  es_principal: boolean;
  activa: boolean;
}

interface CategoriaReceta {
  id: number;
  nombre: string;
  descripcion: string | null;
  orden?: number | null;
  color?: string | null;
  activo?: boolean | null;
  // Bloque operativo (desayuno/almuerzo/cena/bebida) — backfill migración 2026-06-10.
  bloque?: string | null;
  // Filas/opciones que admite el tipo en una minuta (CP=2, CENA=2, resto=1).
  filas_default?: number | null;
}

interface ClienteOperacionRaw {
  id?: number;
  nombre: string;
  es_principal: boolean;
  activa?: boolean;
  direccion?: Record<string, unknown> | null;
  categorias_acceso: unknown;
}

interface ClienteCompleto extends Cliente {
  direcciones: ClienteDireccion[];
  categorias: CategoriaReceta[];
  // FASE B 2026-06-11: el GET /api/clientes/:id ahora trae operaciones[]
  // con dirección embebida y contrato v2. El wizard lo lee para hidratar.
  operaciones?: ClienteOperacionRaw[];
  usuario?: {
    id: number;
    nombre: string;
    email: string;
  };
}

// Componente principal de Clientes
const Clientes: React.FC = () => {
  const { success: _toastSuccess } = useToast();

  // Estados
  const [vistaActiva, setVistaActiva] = useState<
    'lista' | 'crear' | 'editar' | 'detalle'
  >('lista');
  const [clienteSeleccionado, setClienteSeleccionado] =
    useState<ClienteCompleto | null>(null);
  // Default = solo activos. El selector "Todos los estados / Activos /
  // Inactivos" sigue disponible para ver los archivados cuando haga falta.
  const [filtros, setFiltros] = useState({
    busqueda: '',
    tipoCliente: '',
    categoria: '',
    activo: 'true',
  });

  // Estados para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [elementosPorPagina] = useState(10);

  // Hook para manejar clientes
  const {
    clientes,
    loading,
    error,
    createCliente: _createCliente,
    updateCliente: _updateCliente,
    deleteCliente: _deleteCliente,
    refetch: _refetch,
  } = useClientes({
    search: filtros.busqueda || undefined,
    tipo_cliente:
      (filtros.tipoCliente as 'persona_natural' | 'juridica') || undefined,
    activo: filtros.activo ? filtros.activo === 'true' : undefined,
    // El backend por default lista con LIMIT 10 y `useClientes` solo
    // consume `data` (descarta `pagination.total`), así que si no le
    // pedimos un limit alto explicito el frontend cree que el total
    // es 10. Traemos hasta 500 y paginamos localmente — 500 cubre con
    // holgura el volumen de clientes activos actual (17) y futuro.
    limit: 500,
  });

  // Hook para cargar tipos de plato dinámicamente
  const {
    data: tiposPlatoData,
    isLoading: cargandoTiposPlatoRaw,
    error: errorTiposPlato,
  } = useTiposPlato();

  const cargandoTiposPlato: boolean = Boolean(cargandoTiposPlatoRaw);

  // Convertir tipos de plato al formato CategoriaReceta para compatibilidad
  const categoriasRecetas: CategoriaReceta[] = (tiposPlatoData || []).map(
    (tipo: TipoPlato) => ({
      id: tipo.id,
      nombre: tipo.nombre,
      descripcion: tipo.descripcion || null,
      orden: tipo.orden || null,
      color: tipo.color || null,
      activo:
        typeof tipo.activo === 'number'
          ? Boolean(tipo.activo)
          : tipo.activo || null,
      bloque: tipo.bloque ?? null,
      filas_default: tipo.filas_default ?? null,
    })
  );

  // Convertir clientes del API a formato completo temporal
  const clientesData: ClienteCompleto[] = clientes.map((cliente) => ({
    ...cliente,
    categorias_acceso: [], // TODO: obtener categorías de la API
    direcciones: [], // TODO: obtener direcciones de la API
    categorias: [], // TODO: mapear categorías
    created_at: new Date(cliente.created_at),
    updated_at: new Date(cliente.updated_at),
  }));

  // Filtrar clientes adicionales (los filtros principales ya se aplican en el hook)
  const clientesFiltrados = useMemo(() => {
    return clientesData.filter((cliente) => {
      if (!filtros.categoria) return true;
      // El contrato puede venir en shape v2 o legacy number[]; normalizamos.
      const contrato = parseCategoriasAccesoV2(cliente.categorias_acceso);
      return (
        contrato?.tipos.includes(parseInt(filtros.categoria, 10)) ?? false
      );
    });
  }, [clientesData, filtros.categoria]);

  // Calcular paginación
  const totalPaginas = Math.ceil(clientesFiltrados.length / elementosPorPagina);
  const indiceInicio = (paginaActual - 1) * elementosPorPagina;
  const indiceFin = indiceInicio + elementosPorPagina;
  const clientesPaginados = clientesFiltrados.slice(indiceInicio, indiceFin);

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setPaginaActual(1);
  }, [filtros]);

  // Estadísticas
  const estadisticas = useMemo(() => {
    return {
      total: clientesData.length,
      activos: clientesData.filter((c) => c.activo).length,
      inactivos: clientesData.filter((c) => !c.activo).length,
      personasNaturales: clientesData.filter(
        (c) => c.tipo_cliente === 'persona_natural'
      ).length,
      empresas: clientesData.filter((c) => c.tipo_cliente === 'juridica')
        .length,
    };
  }, [clientesData]);

  // Handlers
  const handleCrearCliente = () => {
    setClienteSeleccionado(null);
    setVistaActiva('crear');
  };

  const handleEditarCliente = async (cliente: ClienteCompleto) => {
    // Hidratar cliente desde el endpoint enriquecido (FASE B) — devuelve
    // operaciones[] con dirección embebida + contrato v2. Sin esto, el
    // wizard arrancaría en blanco y guardar borraría todo (TD-030/031).
    try {
      const resp = await clientesAPI.obtener(cliente.id);
      // El backend serializa created_at/updated_at como string; el resto del
      // página espera Date para esos campos. Mezclamos preservando los Date
      // ya construidos en `cliente` y agregando solo los campos nuevos del
      // detalle enriquecido (operaciones, direcciones, …).
      const detalle = (resp?.data ?? null) as unknown as
        | Record<string, unknown>
        | null;
      const enriquecido: ClienteCompleto = detalle
        ? { ...cliente, ...(detalle as Partial<ClienteCompleto>),
            created_at: cliente.created_at, updated_at: cliente.updated_at }
        : cliente;
      setClienteSeleccionado(enriquecido);
    } catch (err) {
      console.error('Error hidratando cliente para edición:', err);
      setClienteSeleccionado(cliente);
    }
    setVistaActiva('editar');
  };

  const handleVerDetalles = (cliente: ClienteCompleto) => {
    setClienteSeleccionado(cliente);
    setVistaActiva('detalle');
  };

  const handleGuardarCliente = async (datosCliente: any) => {
    console.log('Datos del cliente a guardar:', datosCliente);

    // El wizard ahora envía `categorias_acceso` con shape v2
    // (`{ tipos, opciones?, bebidas? }`). El backend acepta v2 y legacy
    // number[] (Zod union en @suminuta/shared::CategoriasAccesoInputSchema),
    // así que no normalizamos acá.
    try {
      if (clienteSeleccionado) {
        // Editar cliente existente
        const clienteActualizado = await _updateCliente(
          clienteSeleccionado.id,
          datosCliente
        );
        if (clienteActualizado) {
          setVistaActiva('lista');
          setClienteSeleccionado(null);
        }
      } else {
        // Crear nuevo cliente
        const nuevoCliente = await _createCliente(datosCliente);
        if (nuevoCliente) {
          setVistaActiva('lista');
          setClienteSeleccionado(null);
        }
      }
    } catch (error) {
      console.error('Error al guardar cliente:', error);
    }
  };

  const handleFiltroChange = (campo: string, valor: string) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const limpiarFiltros = () => {
    // "Limpiar" mantiene el filtro por defecto (Activos) — un usuario
    // que quiera ver archivados ya tiene el selector dedicado y "Limpiar"
    // no es el lugar para revelarlos.
    setFiltros({
      busqueda: '',
      tipoCliente: '',
      categoria: '',
      activo: 'true',
    });
  };

  // Función para obtener el estado del cliente
  const getEstadoCliente = (activo: boolean) => {
    return activo ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
        <CheckCircle className="w-3 h-3 mr-1" />
        Activo
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
        <XCircle className="w-3 h-3 mr-1" />
        Inactivo
      </span>
    );
  };

  // Función para obtener el tipo de cliente
  const getTipoCliente = (tipo: string) => {
    return tipo === 'persona_natural' ? (
      <span className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400">
        <User className="w-4 h-4 mr-1" />
        Particular
      </span>
    ) : (
      <span className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400">
        <Building2 className="w-4 h-4 mr-1" />
        Empresa
      </span>
    );
  };

  if (vistaActiva === 'crear' || vistaActiva === 'editar') {
    // Mostrar spinner mientras cargan los tipos de plato
    if (cargandoTiposPlato) {
      return (
        <div className="p-6 flex justify-center items-center min-h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              Cargando tipos de plato...
            </p>
          </div>
        </div>
      );
    }

    // Mostrar error si falla la carga de tipos de plato
    if (errorTiposPlato) {
      return (
        <div className="p-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6 text-center">
            <XCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-red-900 dark:text-red-300 mb-2">
              Error al cargar tipos de plato
            </h3>
            <p className="text-red-600 dark:text-red-400 mb-4">
              No se pueden crear clientes sin tipos de plato disponibles
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Reintentar
              </button>
              <button
                onClick={() => setVistaActiva('lista')}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-6">
        <ClienteWizard
          cliente={vistaActiva === 'editar' ? clienteSeleccionado : null}
          onGuardar={handleGuardarCliente}
          onCancelar={() => setVistaActiva('lista')}
          categoriasDisponibles={categoriasRecetas}
        />
      </div>
    );
  }

  if (vistaActiva === 'detalle' && clienteSeleccionado) {
    return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Detalles del Cliente
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Información completa del cliente
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleEditarCliente(clienteSeleccionado)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  <span>Editar Cliente</span>
                </button>
                <button
                  onClick={() => setVistaActiva('lista')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                >
                  Volver
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-8">
            {/* Datos Básicos */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Datos Básicos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Nombre
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {clienteSeleccionado.nombre}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    RUT
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {clienteSeleccionado.rut}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Email
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {clienteSeleccionado.email}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Tipo de Cliente
                  </label>
                  <div className="mt-1">
                    {getTipoCliente(clienteSeleccionado.tipo_cliente)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Contacto
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {clienteSeleccionado.nombre_contacto}
                  </p>
                </div>
                {clienteSeleccionado.telefono_contacto && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Teléfono
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {clienteSeleccionado.telefono_contacto}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Estado
                  </label>
                  <div className="mt-1">
                    {getEstadoCliente(clienteSeleccionado.activo)}
                  </div>
                </div>
              </div>
            </div>

            {/* Direcciones */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Direcciones de Entrega
              </h3>
              <div className="space-y-4">
                {clienteSeleccionado.direcciones.map((direccion) => (
                  <div
                    key={direccion.id}
                    className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {direccion.nombre_direccion}
                      </h4>
                      {direccion.es_principal && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                          Principal
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {direccion.direccion}
                      {direccion.comuna && `, ${direccion.comuna}`}
                      {direccion.ciudad && `, ${direccion.ciudad}`}
                      {direccion.codigo_postal &&
                        ` - ${direccion.codigo_postal}`}
                    </p>
                    {direccion.referencia && (
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        <strong>Referencia:</strong> {direccion.referencia}
                      </p>
                    )}
                    {direccion.contacto_nombre && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <strong>Contacto:</strong> {direccion.contacto_nombre}
                        {direccion.contacto_telefono &&
                          ` - ${direccion.contacto_telefono}`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Categorías de Acceso */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Categorías de Acceso
              </h3>
              <div className="flex flex-wrap gap-2">
                {clienteSeleccionado.categorias.map((categoria) => (
                  <span
                    key={categoria.id}
                    className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm"
                  >
                    {categoria.nombre}
                  </span>
                ))}
              </div>
            </div>

            {/* Observaciones */}
            {clienteSeleccionado.observaciones_despacho && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Observaciones de Despacho
                </h3>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {clienteSeleccionado.observaciones_despacho}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader title="Gestión de Clientes" helpSeccion="clientes" />

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8 mt-10">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Clientes
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {estadisticas.total}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Activos
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {estadisticas.activos}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Inactivos
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {estadisticas.inactivos}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <User className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Particulares
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {estadisticas.personasNaturales}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Building2 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Empresas
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {estadisticas.empresas}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controles y Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, RUT o email..."
                  value={filtros.busqueda}
                  onChange={(e) =>
                    handleFiltroChange('busqueda', e.target.value)
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <select
                value={filtros.tipoCliente}
                onChange={(e) =>
                  handleFiltroChange('tipoCliente', e.target.value)
                }
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Todos los tipos</option>
                <option value="persona_natural">Particulares</option>
                <option value="juridica">Empresas</option>
              </select>

              <select
                value={filtros.categoria}
                onChange={(e) =>
                  handleFiltroChange('categoria', e.target.value)
                }
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                disabled={cargandoTiposPlato}
              >
                <option value="">Todas las categorías</option>
                {categoriasRecetas
                  .filter((categoria) => categoria.activo !== false)
                  .map((categoria) => (
                    <option key={categoria.id} value={categoria.id}>
                      {categoria.nombre}
                    </option>
                  ))}
              </select>

              <select
                value={filtros.activo}
                onChange={(e) => handleFiltroChange('activo', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Todos los estados</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>

              <button
                onClick={limpiarFiltros}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Limpiar filtros
              </button>

              <button
                onClick={handleCrearCliente}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Cliente
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de Clientes */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Categorías
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {clientesPaginados.map((cliente) => (
                <tr
                  key={cliente.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {cliente.nombre}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {cliente.rut}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="flex items-center text-sm text-gray-900 dark:text-white">
                        <Mail className="h-4 w-4 mr-1 text-gray-400" />
                        {cliente.email}
                      </div>
                      {cliente.telefono_contacto && (
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Phone className="h-4 w-4 mr-1 text-gray-400" />
                          {cliente.telefono_contacto}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getTipoCliente(cliente.tipo_cliente)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {cliente.categorias.slice(0, 2).map((categoria) => (
                        <span
                          key={categoria.id}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full"
                        >
                          {categoria.nombre}
                        </span>
                      ))}
                      {cliente.categorias.length > 2 && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-full">
                          +{cliente.categorias.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getEstadoCliente(cliente.activo)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleVerDetalles(cliente)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Ver detalles"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditarCliente(cliente)}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {!loading && !error && clientesFiltrados.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando {indiceInicio + 1} al{' '}
                {Math.min(indiceFin, clientesFiltrados.length)} de{' '}
                {clientesFiltrados.length} clientes
              </div>

              <div className="flex items-center space-x-2">
                {/* Botón página anterior */}
                <button
                  onClick={() => setPaginaActual(paginaActual - 1)}
                  disabled={paginaActual === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>

                {/* Números de página */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                    let pageNum;
                    if (totalPaginas <= 5) {
                      pageNum = i + 1;
                    } else if (paginaActual <= 3) {
                      pageNum = i + 1;
                    } else if (paginaActual >= totalPaginas - 2) {
                      pageNum = totalPaginas - 4 + i;
                    } else {
                      pageNum = paginaActual - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPaginaActual(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          pageNum === paginaActual
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                {/* Botón página siguiente */}
                <button
                  onClick={() => setPaginaActual(paginaActual + 1)}
                  disabled={paginaActual === totalPaginas}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && clientesFiltrados.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No se encontraron clientes
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {Object.values(filtros).some((f) => f !== '')
                ? 'Intenta ajustar los filtros de búsqueda'
                : 'Comienza creando tu primer cliente'}
            </p>
            {Object.values(filtros).every((f) => f === '') && (
              <div className="mt-6">
                <button
                  onClick={handleCrearCliente}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Cliente
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Clientes;
